import {
    Box,
    Typography,
    SelectChangeEvent,
    Button,
    Switch,
    TextField,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { ReactElement, useMemo, useState } from 'react'
import { enqueueSnackbar } from 'notistack'
import { updateApplicationSettings } from '../../../services/applications'
import { updateSettings } from '../../../redux/slices/inTransitSlice'
import { useGetAttributes } from '../../DTStudio/Attributes/hooks/getAttributes'
import {
    InTransitSettingsType,
    InTransitSettingsKeys,
    IPreSelectableMappableAttributesOpition,
    IEntities,
} from './interfaces'
import {
    findEntityById,
    flattenEntityTemplates,
} from '../../../utils/EntityUtils'
import GenericDropdown from './GenericDropdown'
import { IAttribute } from '../../DTStudio/interfaces'
import {
    isChartable,
    isEntity,
    isGeoFence,
    isGPS,
    isJson,
    isMappable,
    isMappableEntityAttribute,
} from '../../../utils/AttributeUtils'
import MultiSelectInput from '../../Common/MultiSelectInput'
import {
    inTransitEntitiesTree,
    mandatoryInTransitSettingsKeys,
} from '../../../constants/constants'
import { IKeyValuePair } from '../../Entity/interfaces'
import { useTranslation } from 'react-i18next'

/**
 *
 * @returns Configures the Settings for Cold Chain Logistics Application
 * TODO: Use the inTransitEntitiesTree object and try to recursively generate all the dropdown settings.
 * This will reduce some code duplication like useGetAttributes, GenericDropdown.
 * Create an array of objects where each object will have a label, id, name for each dropdown
 */
interface SettingsPropsType {
    hasWriteAccess: boolean
}

const Settings = (props: SettingsPropsType): ReactElement => {
    const { hasWriteAccess } = props
    const { t } = useTranslation()
    const navSlice = useAppSelector((state) => state.nav)
    const org = useAppSelector((state) => state.org.org)
    const inTransitSlice = useAppSelector((state) => state.inTransit)
    const orgId = org?.id ? org.id : ''
    const { navList } = navSlice

    const [settings, setSettings] = useState<InTransitSettingsType>(
        inTransitSlice.inTransitSettings,
    )
    const shipmentAttributes = inTransitSlice.shipmentAttributes
    const shipmentRelations = inTransitSlice.shipmentRelations
    const [hasClickedSaveButtonWithErrors, setHasClickedSaveButtonWithErrors] =
        useState(false)
    const dispatch = useAppDispatch()

    const entityTypes = flattenEntityTemplates(navList)

    /**
     * TODO: Implement this kind of onChange event handler in Facility Monitoring. Also replace the dropdown
     * selections in Facility Monitoring with GenericDropdown. This way we can reduce the code size in
     * Facility Monitoring Settings.
     */
    const onChangeSettings = (event: SelectChangeEvent<string>) => {
        const resetAttributes = (
            entityTree: IEntities,
            targetEntityTemplateName: string,
            relatedSettings: InTransitSettingsType = {} as InTransitSettingsType, //Think of a better name for relatedSettings
        ): InTransitSettingsType => {
            for (const entityTemplateName in entityTree) {
                const entity = entityTree[entityTemplateName]

                // Check if this is the target entity
                if (entityTemplateName === targetEntityTemplateName) {
                    entity.attributes.forEach((attribute) => {
                        relatedSettings[attribute as InTransitSettingsKeys] = ''
                    })

                    // Reset relations for the target entity
                    if (entity.relations) {
                        Object.keys(entity.relations).forEach((relation) => {
                            relatedSettings[relation as InTransitSettingsKeys] =
                                ''
                            resetAttributes(
                                entity.relations!,
                                relation,
                                relatedSettings,
                            )
                        })
                    }
                } else if (entity.relations) {
                    // Recursively search within relations
                    resetAttributes(
                        entity.relations,
                        targetEntityTemplateName,
                        relatedSettings,
                    )
                }
            }
            return relatedSettings
        }

        const relatedSettings = resetAttributes(
            inTransitEntitiesTree,
            event.target.name,
        )

        setSettings((settings) => ({
            ...settings,
            [event.target.name as InTransitSettingsKeys]: event.target.value,
            ...relatedSettings,
        }))
    }

    const isValueMandatory = (value: string) =>
        mandatoryInTransitSettingsKeys.includes(value as InTransitSettingsKeys)
    const hasDropdownError = (value: string) =>
        hasClickedSaveButtonWithErrors &&
        isValueMandatory(value) &&
        !settings[value as InTransitSettingsKeys]

    const handleSettingsReset = () => {
        setSettings(inTransitSlice.inTransitSettings)
    }

    const handleSave = async () => {
        const areAllMandatoryKeysSet: boolean =
            mandatoryInTransitSettingsKeys.every((key) => !!settings[key])
        if (!areAllMandatoryKeysSet) {
            setHasClickedSaveButtonWithErrors(true)
            enqueueSnackbar(t('Please fill mandatory settings'), {
                variant: 'error',
            })
            return
        }
        setHasClickedSaveButtonWithErrors(false)
        dispatch(updateSettings(settings))
        /**
         * Reset the settings whenever settings keys are changed in UI. And we also need to remove old keys in database.
         * This can be done by calling the below API with empty payload({} as InTransitSettingsType). Later on we can
         * send the real payload with updated keys.
         */
        await updateApplicationSettings<InTransitSettingsType>(
            orgId,
            'inTransit',
            // {} as InTransitSettingsType,
            settings,
        )
        enqueueSnackbar(t('settingsSavedSuccess'), { variant: 'success' })
    }

    const {
        enable = 'false',
        defaultMapLayout = 'both',
        shipmentRefreshInterval = '',
        displayAddressInTooltip = 'false',
        enablePublicShipmentSearch = 'true',
        filterOutlierLocations = 'true',
        analyticsDashboardId = '',
        reportsEntityTemplateId = '',
        reportDataFromAttributeId = '',
        reportDataUntilAttributeId = '',
        reportingObjectAttributeId = '',
        reportURLAttributeId = '',
        shipmentEntityTemplateId = '',
        locationAttributeId = '',
        shipmentIdAttributeId = '',
        shipmentNameAttributeId = '',
        shipmentStatusAttributeId = '',
        alarmStatusAttributeId = '',
        wayPointsAttributeId = '',
        actualArrivalDateAttributeId = '',
        actualDepartureDateAttributeId = '',
        dateFilter = '',
        preSelectedShipmentStatuses = '[]',
        originAttributeId = '',
        destinationAttributeId = '',
        lastKnowAddressAttributeId = 'lastKnownAddress',
        lastReadingAttributeId = 'lastReading',
        reportIdAttributeId = '',
        insuranceEntityTemplateId = '',
        insuredValueAttributeId = '',
        quotePremiumAmountAttributeId = '',
        legalAssuredNameAttributeId = '',
        insuranceStatusAttributeId = '',
        certificateUrlAttributeId = '',
        searatesEntityTemplateId = '',
        AISLocationAttributeId = '',
        pinLocationAttributeId = '',
        locationsGeoJSONAttributeId = '',
        pathGeoJSONAttributeId = '',
        deviceEntityTemplateId = '',
        deviceLocationAttributeId = '',
        containersAttributeId = '',
        vesselsAttributeId = '',
        locationsAttributeId = '',
        routeAttributeId = '',
        carbonEmissionsEntityTemplateId = '',
        totalEmissionsAttributeId = '',
        reportFileAttributeId = '',
        emissionsAttriuteId = '',
        preSelectedMappableAttributes = '[]',
        preSelectedChartableAttributes = '[]',
    } = settings || {}

    const {
        data: insuranceAttributes = [],
        // isError: isLoadingInsuranceAttributesError,
        // isFetching: isFetchingInsuranceAttributes,
        isLoading: isLoadingInsuranceAttributes,
    } = useGetAttributes(orgId, insuranceEntityTemplateId, {
        includeChildAttributes: true,
    })
    const {
        data: searatesAttributes = [],
        // isError: isLoadingSearatesAttributesError,
        // isFetching: isFetchingSearatesAttributes,
        isLoading: isLoadingSearatesAttributes,
    } = useGetAttributes(orgId, searatesEntityTemplateId)
    const {
        data: deviceAttributes = [],
        // isError: isLoadingDeviceAttributesError,
        // isFetching: isFetchingDeviceAttributes,
        isLoading: isLoadingDeviceAttributes,
    } = useGetAttributes(orgId, deviceEntityTemplateId)
    const {
        data: carbonEmissionsAttributes = [],
        // isError: isLoadingCarbonEmissionsAttributesError,
        // isFetching: isFetchingCarbonEmissionsAttributes,
        isLoading: isLoadingCarbonEmissionsAttributes,
    } = useGetAttributes(orgId, carbonEmissionsEntityTemplateId)

    const {
        data: reportsAttributes = [],
        // isError: isLoadingReportsAttributesError,
        // isFetching: isFetchingReportsAttributes,
        isLoading: isLoadingReportsAttributes,
    } = useGetAttributes(orgId, reportsEntityTemplateId)

    const entityTemplatesRelatedToShipment = shipmentRelations.flatMap(
        (relation) => {
            const match = findEntityById(navList, relation.childEntity).entity
            return match ? [match] : []
        },
    )

    const parsedPreSelectedChartableAttributes: string[] = useMemo(() => {
        const preselectedAttributes: string[] = JSON.parse(
            preSelectedChartableAttributes?.trim() || '[]',
        )
        /**
         * From the already preselected attributes Inlcude only the attributes of current selected
         * shipmentEntityTemplateId, searatesEntityTemplateId and deviceEntityTemplateId
         */
        return preselectedAttributes.filter(
            (option: string) =>
                (!!shipmentEntityTemplateId &&
                    option.includes(shipmentEntityTemplateId)) ||
                (!!searatesEntityTemplateId &&
                    option.includes(searatesEntityTemplateId)) ||
                (!!deviceEntityTemplateId &&
                    option.includes(deviceEntityTemplateId)),
        )
    }, [
        preSelectedChartableAttributes,
        shipmentEntityTemplateId,
        searatesEntityTemplateId,
        deviceEntityTemplateId,
    ])
    const parsedPreSelectedMappableAttributes: string[] = useMemo(() => {
        const preselectedAttributes: string[] = JSON.parse(
            preSelectedMappableAttributes?.trim() || '[]',
        )
        /**
         * From the already preselected attributes inlcude only the attributes of current selected
         * shipmentEntityTemplateId, searatesEntityTemplateId and deviceEntityTemplateId
         */
        return preselectedAttributes.filter(
            (option: string) =>
                (!!shipmentEntityTemplateId &&
                    option.includes(shipmentEntityTemplateId)) ||
                (!!searatesEntityTemplateId &&
                    option.includes(searatesEntityTemplateId)) ||
                (!!deviceEntityTemplateId &&
                    option.includes(deviceEntityTemplateId)),
        )
    }, [
        preSelectedMappableAttributes,
        shipmentEntityTemplateId,
        searatesEntityTemplateId,
        deviceEntityTemplateId,
    ])

    const preSelectableMappableAttributesOpitions: IPreSelectableMappableAttributesOpition[] =
        []
    const preSelectableChartableAttributesOptions: IPreSelectableMappableAttributesOpition[] =
        []

    const preparePreSelectOptions = (
        attributes: IAttribute[],
        entityTemplateId: string,
        includeMappableAttributes = true,
    ) => {
        const entityTemplateName =
            findEntityById(entityTypes, entityTemplateId).name || ''

        attributes.forEach((attribute) => {
            if (
                (isMappable(attribute) ||
                    isMappableEntityAttribute(attribute)) &&
                includeMappableAttributes
            ) {
                preSelectableMappableAttributesOpitions.push({
                    id: `${attribute.id} (${entityTemplateId})`,
                    name: `${attribute.name} (${entityTemplateName})`,
                })
            }
            if (isChartable(attribute)) {
                preSelectableChartableAttributesOptions.push({
                    id: `${attribute.id} (${entityTemplateId})`,
                    name: `${attribute.name} (${entityTemplateName})`,
                })
            }
        })
    }

    preparePreSelectOptions(shipmentAttributes, shipmentEntityTemplateId)
    preparePreSelectOptions(deviceAttributes, deviceEntityTemplateId, false)

    const deserializedPreselectedShipmentStatuses: string[] = useMemo(() => {
        return JSON.parse(preSelectedShipmentStatuses?.trim() || '[]')
    }, [preSelectedShipmentStatuses])
    const shipmentStatusOptions: IKeyValuePair[] = useMemo(() => {
        const serializedOptions =
            shipmentAttributes
                ?.find((i) => i.id === shipmentStatusAttributeId)
                ?.range?.trim() || '[]'
        return JSON.parse(serializedOptions)
    }, [shipmentAttributes, shipmentStatusAttributeId])

    const currentLocationOptions = useMemo(
        () => shipmentAttributes.filter(isGPS),
        [shipmentAttributes],
    )

    const entityTypeShipmentAttributes = useMemo(
        () => shipmentAttributes.filter(isEntity),
        [shipmentAttributes],
    )

    const gpsTypeSearatesAttributes = useMemo(
        () => searatesAttributes.filter(isGPS),
        [searatesAttributes],
    )

    const geoFenceTypeSearatesAttributes = useMemo(
        () => searatesAttributes.filter(isGeoFence),
        [searatesAttributes],
    )

    const jsonTypeSearatesAttributes = useMemo(
        () => searatesAttributes.filter(isJson),
        [searatesAttributes],
    )

    const gpsTypeDeviceAttributes = useMemo(
        () => deviceAttributes.filter(isGPS),
        [deviceAttributes],
    )

    const defaultMapLayoutOptions = useMemo(
        () => [
            { id: 'both', name: t('mapAndTable') },
            { id: 'table-only', name: t('tableOnly') },
        ],
        [t],
    )

    const shipmentRefreshIntervalOptions = useMemo(
        () => [
            { id: '', name: 'refreshIntervalOff' },
            { id: '5', name: 'refreshInterval5Min' },
            { id: '10', name: 'refreshInterval10Min' },
            { id: '15', name: 'refreshInterval15Min' },
        ],
        [],
    )

    return (
        <Box
            sx={{
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
            }}
        >
            <Box
                sx={{
                    width: '500px',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('enableColdChainLogistics')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <Switch
                            id="intransit-settings-switch-enable"
                            checked={enable === 'true'}
                            disabled={!hasWriteAccess}
                            onChange={(event) =>
                                setSettings((settings) => {
                                    return {
                                        ...settings,
                                        enable: event.target.checked.toString(),
                                    }
                                })
                            }
                            inputProps={{
                                'aria-label': t('enableColdChainLogistics'),
                            }}
                        />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('displayAddressInTooltip')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <Switch
                            id="intransit-settings-switch-display-address"
                            checked={displayAddressInTooltip === 'true'}
                            disabled={!hasWriteAccess}
                            onChange={(event) =>
                                setSettings((settings) => {
                                    return {
                                        ...settings,
                                        displayAddressInTooltip:
                                            event.target.checked.toString(),
                                    }
                                })
                            }
                            inputProps={{
                                'aria-label': t('displayAddressInTooltip'),
                            }}
                        />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('enablePublicShipmentSearch')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <Switch
                            id="intransit-settings-switch-public-shipment-search"
                            checked={enablePublicShipmentSearch === 'true'}
                            disabled={!hasWriteAccess}
                            onChange={(event) =>
                                setSettings((settings) => {
                                    return {
                                        ...settings,
                                        enablePublicShipmentSearch:
                                            event.target.checked.toString(),
                                    }
                                })
                            }
                            inputProps={{
                                'aria-label': t('enablePublicShipmentSearch'),
                            }}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('filterOutlierLocations')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <Switch
                            id="intransit-settings-switch-filter-outlier"
                            checked={filterOutlierLocations === 'true'}
                            disabled={!hasWriteAccess}
                            onChange={(event) =>
                                setSettings((settings) => {
                                    return {
                                        ...settings,
                                        filterOutlierLocations:
                                            event.target.checked.toString(),
                                    }
                                })
                            }
                            inputProps={{
                                'aria-label': t('filterOutlierLocations'),
                            }}
                        />
                    </Box>
                </Box>

                <GenericDropdown
                    label={t('defaultMapLayout')}
                    name="defaultMapLayout"
                    value={defaultMapLayout}
                    options={defaultMapLayoutOptions}
                    isLoading={false}
                    onChange={onChangeSettings}
                    id="intransit-settings-select-default-map-layout"
                    disabled={!hasWriteAccess}
                />

                <GenericDropdown
                    label={t('shipmentRefreshInterval')}
                    name="shipmentRefreshInterval"
                    value={shipmentRefreshInterval}
                    options={shipmentRefreshIntervalOptions}
                    isLoading={false}
                    onChange={onChangeSettings}
                    id="intransit-settings-select-shipment-refresh-interval"
                    disabled={!hasWriteAccess}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('biDashboardId')}
                        {isValueMandatory('analyticsDashboardId') && '*'}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <TextField
                            id="intransit-settings-textfield-analytics-dashboard-id"
                            name="analyticsDashboardId"
                            label={`${t('biDashboardId')}${isValueMandatory('analyticsDashboardId') ? '*' : ''}`}
                            variant="outlined"
                            value={analyticsDashboardId}
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                                setSettings((settings) => ({
                                    ...settings,
                                    analyticsDashboardId: event.target.value,
                                }))
                            }}
                            fullWidth
                            error={hasDropdownError('analyticsDashboardId')}
                            autoComplete="off"
                            disabled={!hasWriteAccess}
                        />
                    </Box>
                </Box>
                <GenericDropdown
                    label={t('shipmentEntityTemplate')}
                    name="shipmentEntityTemplateId"
                    value={shipmentEntityTemplateId}
                    options={entityTypes}
                    isLoading={false}
                    onChange={onChangeSettings}
                    isMandatory={isValueMandatory('shipmentEntityTemplateId')}
                    hasError={hasDropdownError('shipmentEntityTemplateId')}
                    id="intransit-settings-select-shipment-entity-template"
                    disabled={!hasWriteAccess}
                />
                {shipmentEntityTemplateId && (
                    <>
                        <GenericDropdown
                            label={t('currentLocationAttribute')}
                            name="locationAttributeId"
                            value={locationAttributeId}
                            options={currentLocationOptions}
                            isLoading={false}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'locationAttributeId',
                            )}
                            hasError={hasDropdownError('locationAttributeId')}
                            id="intransit-settings-select-location-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('shipmentIdAttribute')}
                            name="shipmentIdAttributeId"
                            value={shipmentIdAttributeId}
                            options={shipmentAttributes}
                            isLoading={false}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'shipmentIdAttributeId',
                            )}
                            hasError={hasDropdownError('shipmentIdAttributeId')}
                            id="intransit-settings-select-shipment-id-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('shipmentNameAttribute')}
                            name="shipmentNameAttributeId"
                            value={shipmentNameAttributeId}
                            options={shipmentAttributes}
                            isLoading={false}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'shipmentNameAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'shipmentNameAttributeId',
                            )}
                            id="intransit-settings-select-shipment-name-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('shipmentStatusAttribute')}
                            name="shipmentStatusAttributeId"
                            value={shipmentStatusAttributeId}
                            options={shipmentAttributes}
                            isLoading={false}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'shipmentStatusAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'shipmentStatusAttributeId',
                            )}
                            id="intransit-settings-select-shipment-status-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('alarmStatusAttribute')}
                            name="alarmStatusAttributeId"
                            value={alarmStatusAttributeId}
                            options={shipmentAttributes}
                            isLoading={false}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'alarmStatusAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'alarmStatusAttributeId',
                            )}
                            id="intransit-settings-select-alarm-status-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('wayPointsAttribute')}
                            name="wayPointsAttributeId"
                            value={wayPointsAttributeId}
                            options={shipmentAttributes}
                            isLoading={false}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'wayPointsAttributeId',
                            )}
                            hasError={hasDropdownError('wayPointsAttributeId')}
                            id="intransit-settings-select-way-points-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('actualDepartureDateAttribute')}
                            name="actualDepartureDateAttributeId"
                            value={actualDepartureDateAttributeId}
                            options={shipmentAttributes}
                            isLoading={false}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'actualDepartureDateAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'actualDepartureDateAttributeId',
                            )}
                            id="intransit-settings-select-actual-departure-date-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('actualArrivalDateAttribute')}
                            name="actualArrivalDateAttributeId"
                            value={actualArrivalDateAttributeId}
                            options={shipmentAttributes}
                            isLoading={false}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'actualArrivalDateAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'actualArrivalDateAttributeId',
                            )}
                            id="intransit-settings-select-actual-arrival-date-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('Date Filter Attribute')}
                            name="dateFilter"
                            value={dateFilter}
                            options={shipmentAttributes.filter(
                                (i) =>
                                    i.id === 'actualArrivalDate' ||
                                    i.id === 'actualDepartureDate',
                            )}
                            isLoading={false}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory('dateFilter')}
                            hasError={hasDropdownError('dateFilter')}
                            id="intransit-settings-select-date-filter-attribute"
                            disabled={!hasWriteAccess}
                        />

                        <GenericDropdown
                            label={t('originAttribute')}
                            name="originAttributeId"
                            value={originAttributeId}
                            options={entityTypeShipmentAttributes}
                            isLoading={false}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory('originAttributeId')}
                            hasError={hasDropdownError('originAttributeId')}
                            id="intransit-settings-select-origin-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('destinationAttribute')}
                            name="destinationAttributeId"
                            value={destinationAttributeId}
                            options={entityTypeShipmentAttributes}
                            isLoading={false}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'destinationAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'destinationAttributeId',
                            )}
                            id="intransit-settings-select-destination-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('lastKnowAddressAttribute')}
                            name="lastKnowAddressAttributeId"
                            value={lastKnowAddressAttributeId}
                            options={shipmentAttributes}
                            isLoading={false}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'lastKnowAddressAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'lastKnowAddressAttributeId',
                            )}
                            id="intransit-settings-select-last-know-address-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('lastReadingAttribute')}
                            name="lastReadingAttributeId"
                            value={lastReadingAttributeId}
                            options={shipmentAttributes}
                            isLoading={false}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'lastReadingAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'lastReadingAttributeId',
                            )}
                            id="intransit-settings-select-last-reading-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('reportIdAttribute')}
                            name="reportIdAttributeId"
                            value={reportIdAttributeId}
                            options={shipmentAttributes}
                            isLoading={false}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'reportIdAttributeId',
                            )}
                            hasError={hasDropdownError('reportIdAttributeId')}
                            id="intransit-settings-select-report-id-attribute"
                            disabled={!hasWriteAccess}
                        />
                    </>
                )}
                <GenericDropdown
                    label={t('insuranceEntityTemplate')}
                    name="insuranceEntityTemplateId"
                    value={insuranceEntityTemplateId}
                    options={entityTemplatesRelatedToShipment}
                    isLoading={false}
                    onChange={onChangeSettings}
                    isMandatory={isValueMandatory('insuranceEntityTemplateId')}
                    hasError={hasDropdownError('insuranceEntityTemplateId')}
                    id="intransit-settings-select-insurance-entity-template"
                    disabled={!hasWriteAccess}
                />
                {insuranceEntityTemplateId && (
                    <>
                        <GenericDropdown
                            label={t('insuredValueAttribute')}
                            name="insuredValueAttributeId"
                            value={insuredValueAttributeId}
                            options={insuranceAttributes}
                            isLoading={isLoadingInsuranceAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'insuredValueAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'insuredValueAttributeId',
                            )}
                            id="intransit-settings-select-insured-value-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('quotePremiumAmountAttribute')}
                            name="quotePremiumAmountAttributeId"
                            value={quotePremiumAmountAttributeId}
                            options={insuranceAttributes}
                            isLoading={isLoadingInsuranceAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'quotePremiumAmountAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'quotePremiumAmountAttributeId',
                            )}
                            id="intransit-settings-select-quote-premium-amount-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('legalAssuredNameAttribute')}
                            name="legalAssuredNameAttributeId"
                            value={legalAssuredNameAttributeId}
                            options={insuranceAttributes}
                            isLoading={isLoadingInsuranceAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'legalAssuredNameAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'legalAssuredNameAttributeId',
                            )}
                            id="intransit-settings-select-legal-assured-name-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('insuranceStatusAttribute')}
                            name="insuranceStatusAttributeId"
                            value={insuranceStatusAttributeId}
                            options={insuranceAttributes}
                            isLoading={isLoadingInsuranceAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'insuranceStatusAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'insuranceStatusAttributeId',
                            )}
                            id="intransit-settings-select-insurance-status-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('certificateUrlAttribute')}
                            name="certificateUrlAttributeId"
                            value={certificateUrlAttributeId}
                            options={insuranceAttributes}
                            isLoading={isLoadingInsuranceAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'certificateUrlAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'certificateUrlAttributeId',
                            )}
                            id="intransit-settings-select-certificate-url-attribute"
                            disabled={!hasWriteAccess}
                        />
                    </>
                )}
                <GenericDropdown
                    label={t('searatesEntityTemplate')}
                    name="searatesEntityTemplateId"
                    value={searatesEntityTemplateId}
                    options={entityTemplatesRelatedToShipment}
                    isLoading={false}
                    onChange={onChangeSettings}
                    isMandatory={isValueMandatory('searatesEntityTemplateId')}
                    hasError={hasDropdownError('searatesEntityTemplateId')}
                    id="intransit-settings-select-searates-entity-template"
                    disabled={!hasWriteAccess}
                />
                {searatesEntityTemplateId && (
                    <>
                        <GenericDropdown
                            label={t('aisLocationAttribute')}
                            name="AISLocationAttributeId"
                            value={AISLocationAttributeId}
                            options={gpsTypeSearatesAttributes}
                            isLoading={isLoadingSearatesAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'AISLocationAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'AISLocationAttributeId',
                            )}
                            id="intransit-settings-select-ais-location-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('pinLocationAttribute')}
                            name="pinLocationAttributeId"
                            value={pinLocationAttributeId}
                            options={gpsTypeSearatesAttributes}
                            isLoading={isLoadingSearatesAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'pinLocationAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'pinLocationAttributeId',
                            )}
                            id="intransit-settings-select-pin-location-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('portsAttribute')}
                            name="locationsGeoJSONAttributeId"
                            value={locationsGeoJSONAttributeId}
                            options={geoFenceTypeSearatesAttributes}
                            isLoading={isLoadingSearatesAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'locationsGeoJSONAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'locationsGeoJSONAttributeId',
                            )}
                            id="intransit-settings-select-ports-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('estimatedPathAttribute')}
                            name="pathGeoJSONAttributeId"
                            value={pathGeoJSONAttributeId}
                            options={geoFenceTypeSearatesAttributes}
                            isLoading={isLoadingSearatesAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'pathGeoJSONAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'pathGeoJSONAttributeId',
                            )}
                            id="intransit-settings-select-estimated-path-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('containersAttribute')}
                            name="containersAttributeId"
                            value={containersAttributeId}
                            options={jsonTypeSearatesAttributes}
                            isLoading={isLoadingSearatesAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'containersAttributeId',
                            )}
                            hasError={hasDropdownError('containersAttributeId')}
                            id="intransit-settings-select-containers-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('vesselsAttribute')}
                            name="vesselsAttributeId"
                            value={vesselsAttributeId}
                            options={jsonTypeSearatesAttributes}
                            isLoading={isLoadingSearatesAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory('vesselsAttributeId')}
                            hasError={hasDropdownError('vesselsAttributeId')}
                            id="intransit-settings-select-vessels-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('locationsAttribute')}
                            name="locationsAttributeId"
                            value={locationsAttributeId}
                            options={jsonTypeSearatesAttributes}
                            isLoading={isLoadingSearatesAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'locationsAttributeId',
                            )}
                            hasError={hasDropdownError('locationsAttributeId')}
                            id="intransit-settings-select-locations-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('routeAttribute')}
                            name="routeAttributeId"
                            value={routeAttributeId}
                            options={jsonTypeSearatesAttributes}
                            isLoading={isLoadingSearatesAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory('routeAttributeId')}
                            hasError={hasDropdownError('routeAttributeId')}
                            id="intransit-settings-select-route-attribute"
                            disabled={!hasWriteAccess}
                        />
                    </>
                )}
                <GenericDropdown
                    label={t('deviceEntityTemplate')}
                    name="deviceEntityTemplateId"
                    value={deviceEntityTemplateId}
                    options={entityTemplatesRelatedToShipment}
                    isLoading={false}
                    onChange={onChangeSettings}
                    isMandatory={isValueMandatory('deviceEntityTemplateId')}
                    hasError={hasDropdownError('deviceEntityTemplateId')}
                    id="intransit-settings-select-device-entity-template"
                    disabled={!hasWriteAccess}
                />
                {deviceEntityTemplateId && (
                    <GenericDropdown
                        label={t('deviceLocationAttribute')}
                        name="deviceLocationAttributeId"
                        value={deviceLocationAttributeId}
                        options={gpsTypeDeviceAttributes}
                        isLoading={isLoadingDeviceAttributes}
                        onChange={onChangeSettings}
                        isMandatory={isValueMandatory(
                            'deviceLocationAttributeId',
                        )}
                        hasError={hasDropdownError('deviceLocationAttributeId')}
                        id="intransit-settings-select-device-location-attribute"
                        disabled={!hasWriteAccess}
                    />
                )}
                <GenericDropdown
                    label={t('carbonEmissionsEntityTemplate')}
                    name="carbonEmissionsEntityTemplateId"
                    value={carbonEmissionsEntityTemplateId}
                    options={entityTemplatesRelatedToShipment}
                    isLoading={false}
                    onChange={onChangeSettings}
                    isMandatory={isValueMandatory(
                        'carbonEmissionsEntityTemplateId',
                    )}
                    hasError={hasDropdownError(
                        'carbonEmissionsEntityTemplateId',
                    )}
                    id="intransit-settings-select-carbon-emissions-entity-template"
                    disabled={!hasWriteAccess}
                />
                {carbonEmissionsEntityTemplateId && (
                    <>
                        <GenericDropdown
                            label={t('totalEmissionsAttribute')}
                            name="totalEmissionsAttributeId"
                            value={totalEmissionsAttributeId}
                            options={carbonEmissionsAttributes}
                            isLoading={isLoadingCarbonEmissionsAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'totalEmissionsAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'totalEmissionsAttributeId',
                            )}
                            id="intransit-settings-select-total-emissions-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('reportFileAttribute')}
                            name="reportFileAttributeId"
                            value={reportFileAttributeId}
                            options={carbonEmissionsAttributes}
                            isLoading={isLoadingCarbonEmissionsAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'reportFileAttributeId',
                            )}
                            hasError={hasDropdownError('reportFileAttributeId')}
                            id="intransit-settings-select-report-file-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('emissionsBySegmentAttribute')}
                            name="emissionsAttriuteId"
                            value={emissionsAttriuteId}
                            options={carbonEmissionsAttributes}
                            isLoading={isLoadingCarbonEmissionsAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'emissionsAttriuteId',
                            )}
                            hasError={hasDropdownError('emissionsAttriuteId')}
                            id="intransit-settings-select-emissions-attribute"
                            disabled={!hasWriteAccess}
                        />
                    </>
                )}
                <GenericDropdown
                    label={t('reportsEntityTemplate')}
                    name="reportsEntityTemplateId"
                    value={reportsEntityTemplateId}
                    options={entityTypes}
                    isLoading={false}
                    onChange={onChangeSettings}
                    isMandatory={isValueMandatory('reportsEntityTemplateId')}
                    hasError={hasDropdownError('reportsEntityTemplateId')}
                    id="intransit-settings-select-reports-entity-template"
                    disabled={!hasWriteAccess}
                />
                {reportsEntityTemplateId && (
                    <>
                        <GenericDropdown
                            label={t('reportDataFromAttribute')}
                            name="reportDataFromAttributeId"
                            value={reportDataFromAttributeId}
                            options={reportsAttributes}
                            isLoading={isLoadingReportsAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'reportDataFromAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'reportDataFromAttributeId',
                            )}
                            id="intransit-settings-select-report-data-from-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('reportDataUntilAttribute')}
                            name="reportDataUntilAttributeId"
                            value={reportDataUntilAttributeId}
                            options={reportsAttributes}
                            isLoading={isLoadingReportsAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'reportDataUntilAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'reportDataUntilAttributeId',
                            )}
                            id="intransit-settings-select-report-data-until-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('reportingObjectAttribute')}
                            name="reportingObjectAttributeId"
                            value={reportingObjectAttributeId}
                            options={reportsAttributes}
                            isLoading={isLoadingReportsAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'reportingObjectAttributeId',
                            )}
                            hasError={hasDropdownError(
                                'reportingObjectAttributeId',
                            )}
                            id="intransit-settings-select-reporting-object-attribute"
                            disabled={!hasWriteAccess}
                        />
                        <GenericDropdown
                            label={t('reportUrlAttribute')}
                            name="reportURLAttributeId"
                            value={reportURLAttributeId}
                            options={reportsAttributes}
                            isLoading={isLoadingReportsAttributes}
                            onChange={onChangeSettings}
                            isMandatory={isValueMandatory(
                                'reportURLAttributeId',
                            )}
                            hasError={hasDropdownError('reportURLAttributeId')}
                            id="intransit-settings-select-report-url-attribute"
                            disabled={!hasWriteAccess}
                        />
                    </>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                        marginTop: '20px',
                    }}
                >
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('preSelectedMappableAttributes')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <MultiSelectInput
                            id="intransit-settings-multiselect-mappable-attributes"
                            label={t('preSelectedMappableAttributes')}
                            ariaLabel={t('preSelectedMappableAttributes')}
                            value={parsedPreSelectedMappableAttributes}
                            onChange={(id: string, value: string[]) => {
                                setSettings((settings) => {
                                    return {
                                        ...settings,
                                        preSelectedMappableAttributes:
                                            JSON.stringify(value),
                                        //  '[]',
                                    }
                                })
                            }}
                            disabled={!hasWriteAccess}
                            list={preSelectableMappableAttributesOpitions.map(
                                (i) => {
                                    return {
                                        id: i.id,
                                        name: i.name,
                                    }
                                },
                            )}
                        />
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                    }}
                >
                    <Typography
                        id="preselected-chartable-label"
                        sx={{ marginRight: '8px', width: '200px' }}
                    >
                        {t('preSelectedChartableAttributes')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <MultiSelectInput
                            id="intransit-settings-multiselect-chartable-attributes"
                            label={t('preSelectedChartableAttributes')}
                            title={t('preSelectedChartableAttributes')}
                            value={parsedPreSelectedChartableAttributes}
                            onChange={(id: string, value: string[]) => {
                                setSettings((settings) => {
                                    return {
                                        ...settings,
                                        preSelectedChartableAttributes:
                                            JSON.stringify(value),
                                        // '[]',
                                    }
                                })
                            }}
                            disabled={!hasWriteAccess}
                            list={preSelectableChartableAttributesOptions.map(
                                (i) => {
                                    return {
                                        id: i.id,
                                        name: i.name,
                                    }
                                },
                            )}
                        />
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                    }}
                >
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('preSelectedShipmentStatuses')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <MultiSelectInput
                            id="intransit-settings-multiselect-shipment-statuses"
                            label={t('preSelectedShipmentStatuses')}
                            value={deserializedPreselectedShipmentStatuses}
                            onChange={(id: string, value: string[]) => {
                                setSettings((settings) => {
                                    return {
                                        ...settings,
                                        preSelectedShipmentStatuses:
                                            JSON.stringify(value),
                                        // '[]',
                                    }
                                })
                            }}
                            disabled={!hasWriteAccess}
                            list={shipmentStatusOptions.map((i) => {
                                return {
                                    id: i.key,
                                    name: i.value,
                                }
                            })}
                        />
                    </Box>
                </Box>

                {hasWriteAccess && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mt: 2,
                        }}
                    >
                        <Button
                            id="intransit-settings-button-reset"
                            variant="contained"
                            color="secondary"
                            onClick={handleSettingsReset}
                            sx={{
                                mr: 2,
                                border: '1px solid #3a5e98',
                                backgroundColor: 'white',
                                color: '#3a5e98',
                            }}
                        >
                            {t('reset')}
                        </Button>
                        <Button
                            id="intransit-settings-button-save"
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            sx={{
                                mr: 2,
                                border: '1px solid #3a5e98',
                                backgroundColor: '#3a5e98',
                                color: 'white',
                            }}
                        >
                            {t('save')}
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default Settings
